import React from 'react';
import { Box, Typography, Card, CardContent, Chip, Link, Grid } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FestivalIcon from '@mui/icons-material/Festival';
import RadioIcon from '@mui/icons-material/Radio';
import VerifiedIcon from '@mui/icons-material/Verified';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

interface ExternalValidationEntry {
  type: 'press_review' | 'award' | 'festival_appearance' | 'radio_airplay' | 'other';
  source: string;
  quote?: string;
  author?: string;
  url?: string;
  date?: Date;
  featured?: boolean;
}

interface PressReviewsTabProps {
  validations: ExternalValidationEntry[];
}

const formatDate = (date?: Date) =>
  date ? new Date(date).toLocaleDateString('en-IE', { year: 'numeric', month: 'long' }) : null;

const cleanQuote = (q: string) => q.replace(/^["""'']+|["""'']+$/g, '').trim();

const SourceLink: React.FC<{ source: string; url?: string }> = ({ source, url }) =>
  url ? (
    <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ color: '#4fc3f7', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      {source}
    </Link>
  ) : <>{source}</>;


// ─── Press Review — full width, journalistic quote treatment ─────────────────
const PressReviewCard: React.FC<{ v: ExternalValidationEntry }> = ({ v }) => (
  <Grid item xs={12}>
    <Card sx={{
      borderLeft: '4px solid',
      borderColor: v.featured ? 'primary.main' : 'primary.dark',
      bgcolor: v.featured ? 'rgba(79, 195, 247, 0.05)' : 'background.paper',
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip icon={<NewspaperIcon />} label="Press Review" color="primary" size="small" />
            {v.featured && <Chip icon={<StarIcon />} label="Featured" color="warning" size="small" variant="outlined" />}
          </Box>
          {v.date && <Typography variant="caption" color="text.secondary">{formatDate(v.date)}</Typography>}
        </Box>

        {v.quote && (
          <Box sx={{ mb: 2, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
            <FormatQuoteIcon sx={{ color: 'primary.main', opacity: 0.4, fontSize: 22, mb: 0.5, display: 'block' }} />
            <Typography variant="body1" fontStyle="italic" sx={{ lineHeight: 1.7 }}>
              "{cleanQuote(v.quote)}"
            </Typography>
          </Box>
        )}

        <Typography variant="subtitle2" color="text.secondary">
          — <SourceLink source={v.source} url={v.url} />
          {v.author && `, ${v.author}`}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
);


// ─── Award — half width, celebratory trophy treatment ────────────────────────
const AwardCard: React.FC<{ v: ExternalValidationEntry }> = ({ v }) => (
  <Grid item xs={12} sm={6}>
    <Card sx={{
      height: '100%',
      borderTop: '4px solid',
      borderColor: v.featured ? 'warning.main' : 'warning.dark',
      bgcolor: v.featured ? 'rgba(255, 193, 7, 0.07)' : 'background.paper',
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <EmojiEventsIcon sx={{ color: 'warning.main', fontSize: 40 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {v.featured && <Chip icon={<StarIcon />} label="Featured" color="warning" size="small" variant="outlined" />}
            {v.date && <Typography variant="caption" color="text.secondary">{new Date(v.date).getFullYear()}</Typography>}
          </Box>
        </Box>

        {/* The award name/title lives in the quote field */}
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ lineHeight: 1.3 }}>
          {v.quote ? cleanQuote(v.quote) : v.source}
        </Typography>

        {v.quote && (
          <Typography variant="body2" color="text.secondary">
            Awarded by <SourceLink source={v.source} url={v.url} />
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grid>
);


// ─── Festival Appearance — half width, performance credit ────────────────────
const FestivalCard: React.FC<{ v: ExternalValidationEntry }> = ({ v }) => (
  <Grid item xs={12} sm={6}>
    <Card sx={{ height: '100%', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip icon={<FestivalIcon />} label="Festival" color="secondary" size="small" />
          {v.date && <Typography variant="caption" color="text.secondary">{formatDate(v.date)}</Typography>}
        </Box>

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Performed at
        </Typography>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <SourceLink source={v.source} url={v.url} />
        </Typography>

        {v.quote && (
          <Typography variant="body2" color="text.secondary">
            {v.quote}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grid>
);


// ─── Radio / Media — half width, broadcast credit ───────────────────────────
const RadioCard: React.FC<{ v: ExternalValidationEntry }> = ({ v }) => (
  <Grid item xs={12} sm={6}>
    <Card sx={{ height: '100%', borderLeft: '4px solid', borderColor: 'info.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip icon={<RadioIcon />} label="Radio / Media" color="info" size="small" />
          {v.date && <Typography variant="caption" color="text.secondary">{formatDate(v.date)}</Typography>}
        </Box>

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Featured on
        </Typography>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <SourceLink source={v.source} url={v.url} />
        </Typography>

        {v.quote && (
          <Typography variant="body2" color="text.secondary">
            {v.quote}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grid>
);


// ─── Other Recognition — half width, clean verification card ────────────────
const OtherCard: React.FC<{ v: ExternalValidationEntry }> = ({ v }) => (
  <Grid item xs={12} sm={6}>
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip icon={<VerifiedIcon />} label="Recognition" color="default" size="small" />
          {v.date && <Typography variant="caption" color="text.secondary">{formatDate(v.date)}</Typography>}
        </Box>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          <SourceLink source={v.source} url={v.url} />
        </Typography>

        {v.quote && (
          <Typography variant="body2" color="text.secondary">
            {v.quote}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grid>
);


// ─── Main tab ────────────────────────────────────────────────────────────────
const PressReviewsTab: React.FC<PressReviewsTabProps> = ({ validations }) => {
  const sorted = [...validations].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    if (a.date && b.date) return new Date(b.date).getTime() - new Date(a.date).getTime();
    if (a.date) return -1;
    if (b.date) return 1;
    return 0;
  });

  const renderEntry = (v: ExternalValidationEntry, i: number) => {
    switch (v.type) {
      case 'press_review':       return <PressReviewCard key={i} v={v} />;
      case 'award':              return <AwardCard key={i} v={v} />;
      case 'festival_appearance':return <FestivalCard key={i} v={v} />;
      case 'radio_airplay':      return <RadioCard key={i} v={v} />;
      default:                   return <OtherCard key={i} v={v} />;
    }
  };

  if (validations.length === 0) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        No press or awards information available.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>Press & Awards</Typography>
      <Grid container spacing={3}>
        {sorted.map((v, i) => renderEntry(v, i))}
      </Grid>
    </Box>
  );
};

export default PressReviewsTab;
